/**
 * Sprint · Mission Architecture Alignment (2026-06-03) — frontend-only mission registry.
 *
 * Why this exists: the backend `loop_taxonomy.py` ships ONE merged mission
 * (`grow-the-brand`) that mixes Brand Studio + Marketing surfaces. Paco's
 * Mission Architecture Alignment sprint requires us to split them into two
 * independent missions WITHOUT touching the backend. This file is the
 * canonical client-side overlay that the consumers (FlowBreadcrumb,
 * MissionPulseHeatmap, TodaysMissionTab, JourneyWheel, BrandStudioPillarsBreadcrumb)
 * use to render the two missions as separate surfaces.
 *
 * Hard rules:
 *   - No backend imports.
 *   - No collection writes.
 *   - Stage → page maps already exist in the backend; this overlay only
 *     reshapes how they group/label.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Mission · Build & Protect the Brand (Brand Studio) — 5 stages
// ─────────────────────────────────────────────────────────────────────────────
export const BUILD_AND_PROTECT_MISSION = {
  id: "build-and-protect-the-brand",
  label: {
    en: "Build & Protect the Brand",
    es: "Construir y Proteger la Marca",
    de: "Marke aufbauen & schützen",
  },
  tagline: {
    en: "Define, protect, strengthen, measure and steward the brand.",
    es: "Define, protege, fortalece, mide y custodia la marca.",
    de: "Marke definieren, schützen, stärken, messen und verwalten.",
  },
  color: "#6366F1", // indigo — mission-level accent (used in "MISSION:" eyebrow)
  icon: "Shield",
  is_magic: false,
  studio_path: "/brand-studio",
  // Per-pillar colors (Sprint 2026-06-11 PM-9, paco-approved). Each pillar
  // tints its own pill so the row reads like the Web Studio / Marketing
  // multi-color pattern instead of one-color-for-all-stages.
  // Sprint 2026-06-11 PM-9 Phase 1D (paco-approved): each stage also
  // ships an `icon` (Lucide name) + `subtitle` for the card-shaped Row-1
  // pill design. UI components import the icon from lucide-react via the
  // name string. Keep subtitles short (~2-3 words) so the pill stays compact.
  stages: [
    {
      id: "foundation",
      color: "#6366F1", // indigo — anchor / canonical brand color
      icon: "Layers",
      subtitle: { en: "Define identity",  es: "Define la identidad",  de: "Identität definieren" },
      label: { en: "Foundation", es: "Cimientos", de: "Fundament" },
      page: "/brand-studio/foundation/setup",
    },
    {
      id: "protect",
      color: "#F43F5E", // rose — defensive / security signal
      icon: "Shield",
      subtitle: { en: "Defend the brand", es: "Defiende la marca",    de: "Marke schützen" },
      label: { en: "Protect", es: "Proteger", de: "Schützen" },
      page: "/brand-studio/protect/identity-analysis",
    },
    {
      id: "strengthen",
      color: "#10B981", // emerald — growth / vitality
      icon: "Sparkles",
      subtitle: { en: "Grow vitality",    es: "Crece con vitalidad",  de: "Vitalität steigern" },
      label: { en: "Strengthen", es: "Fortalecer", de: "Stärken" },
      page: "/brand-studio/strengthen/diagnostic",
    },
    {
      id: "health",
      color: "#F59E0B", // amber — KPI / health monitoring
      icon: "Activity",
      subtitle: { en: "Monitor pulse",    es: "Monitorea el pulso",   de: "Puls überwachen" },
      label: {
        en: "Brand Health",
        es: "Salud de Marca",
        de: "Markengesundheit",
      },
      page: "/brand-studio/brand-health",
    },
    {
      id: "steward",
      color: "#8B5CF6", // violet — custodian / premium
      icon: "Compass",
      subtitle: { en: "Govern legacy",    es: "Gobierna el legado",   de: "Erbe verwalten" },
      label: { en: "Steward", es: "Custodiar", de: "Verwalten" },
      page: "/brand-studio/steward/living-bi",
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Mission · Grow the Brand (Marketing Studio) — 8 stages, Brand surfaces REMOVED
// ─────────────────────────────────────────────────────────────────────────────
export const GROW_THE_BRAND_MISSION = {
  id: "grow-the-brand",
  label: {
    en: "Grow the Brand",
    es: "Hacer Crecer la Marca",
    de: "Die Marke ausbauen",
  },
  tagline: {
    en: "Diagnose, position, build, plan, create, publish, measure, improve.",
    es: "Diagnostica, posiciona, construye, planifica, crea, publica, mide, mejora.",
    de: "Diagnostizieren, positionieren, aufbauen, planen, kreieren, veröffentlichen, messen, verbessern.",
  },
  color: "#EC4899",
  icon: "Sparkles",
  is_magic: true,
  studio_path: "/marketing-studio",
  // Stage page targets are the CANONICAL homes (match loop_taxonomy.GROW_THE_BRAND_STAGES).
  // `owner_studio` drives the cross-studio badge in the Mission Breadcrumb (AO-001):
  //   brand → "in Brand Studio" · creative → "in Creative Studio" · shared → "Marketing execution".
  // marketing = the home studio (no badge).
  stages: [
    { id: "diagnose", label: { en: "Diagnose", es: "Diagnosticar", de: "Diagnostizieren" }, subtitle: { en: "Scan & audit", es: "Escanear y auditar", de: "Scannen & prüfen" }, color: "#F59E0B", icon: "Search",        owner_studio: "brand",     page: "/brand-studio/strengthen/diagnostic", drawable: true },
    { id: "position", label: { en: "Position", es: "Posicionar",   de: "Positionieren" },   subtitle: { en: "Define stance", es: "Definir postura", de: "Haltung definieren" }, color: "#6366F1", icon: "Target",       owner_studio: "brand",     page: "/brand-studio/strengthen/positioning" },
    { id: "build",    label: { en: "Build",    es: "Construir",    de: "Aufbauen" },        subtitle: { en: "Build identity", es: "Construir identidad", de: "Identität bauen" }, color: "#6366F1", icon: "Layers",     owner_studio: "brand",     page: "/brand-studio/foundation/setup" },
    { id: "plan",     label: { en: "Plan",     es: "Planificar",   de: "Planen" },          subtitle: { en: "30-day rhythm", es: "Ritmo de 30 días", de: "30-Tage-Rhythmus" }, color: "#A855F7", icon: "ClipboardList", owner_studio: "marketing", page: "/marketing/plans", drawable: true },
    { id: "create",   label: { en: "Create",   es: "Crear",        de: "Kreieren" },        subtitle: { en: "Ship the work", es: "Lanza el trabajo", de: "Arbeit liefern" },   color: "#3B82F6", icon: "Wand2",        owner_studio: "creative",  page: "/creative-studio", drawable: true },
    { id: "publish",  label: { en: "Publish",  es: "Publicar",     de: "Veröffentlichen" }, subtitle: { en: "Go live", es: "En vivo", de: "Live gehen" },                     color: "#F97316", icon: "Sparkles",     owner_studio: "shared",    page: "/marketing/brand-launch", drawable: true },
    { id: "measure",  label: { en: "Measure",  es: "Medir",        de: "Messen" },          subtitle: { en: "ROI & insights", es: "ROI e insights", de: "ROI & Insights" },   color: "#10B981", icon: "BarChart3",    owner_studio: "marketing", page: "/marketing/roi", drawable: true },
    { id: "improve",  label: { en: "Improve",  es: "Mejorar",      de: "Verbessern" },      subtitle: { en: "Refine & iterate", es: "Refinar e iterar", de: "Verfeinern" }, color: "#06B6D4", icon: "TrendingUp",   owner_studio: "marketing", page: "/marketing/seo-analyzer" },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Layer-2 workflow strips — one per Brand Studio pillar.
// The "items" array is the workflow the operator moves through inside the
// current pillar. Order = founder-approved sidebar order (Sprint 2026-06-03).
// ─────────────────────────────────────────────────────────────────────────────
export const BRAND_PILLAR_WORKFLOWS = {
  foundation: {
    label: { en: "Build the Foundation", es: "Construye los Cimientos", de: "Fundament bauen" },
    // Canonical Foundation Layer-2 order (paco 2026-06-11):
    // Setup → Identity → Voice → Repository → Launch.
    items: [
      { id: "setup",      label: { en: "Brand Setup",      es: "Configuración",      de: "Marken-Setup" },    page: "/brand-studio/foundation/setup" },
      { id: "identity",   label: { en: "Brand Identity",   es: "Identidad de Marca", de: "Markenidentität" }, page: "/brand-studio/foundation/identity" },
      { id: "voice",      label: { en: "Brand Voice",      es: "Voz de Marca",       de: "Markenstimme" },    page: "/brand-studio/foundation/voice" },
      { id: "repository", label: { en: "Brand Repository", es: "Repositorio",        de: "Repository" },      page: "/brand-studio/foundation/repository" },
      { id: "launch",     label: { en: "Brand Launch",     es: "Lanzamiento",        de: "Marken-Launch" },   page: "/brand-studio/foundation/launch" },
    ],
  },
  protect: {
    label: { en: "Protect the Brand", es: "Proteger la Marca", de: "Marke schützen" },
    // Order matches the sidebar (Layout.jsx lines 708-711, 2026-06-03):
    // Brand Identity Analysis → Domain Portfolio → Trademark Readiness → Evidence Center.
    items: [
      { id: "identity-analysis",   label: { en: "Brand Identity Analysis", es: "Análisis de Identidad", de: "Identitäts-Analyse" }, page: "/brand-studio/protect/identity-analysis" },
      { id: "domain-portfolio",    label: { en: "Domain Portfolio",        es: "Portafolio de Dominios", de: "Domain-Portfolio" },  page: "/brand-studio/protect/domain-portfolio" },
      { id: "trademark-readiness", label: { en: "Trademark Readiness",     es: "Listo para Marca",       de: "Marken-Bereitschaft" }, page: "/brand-studio/protect/trademark-readiness" },
      { id: "evidence-center",     label: { en: "Evidence Center",         es: "Centro de Evidencia",    de: "Beweis-Center" },     page: "/brand-studio/protect/evidence-center" },
    ],
  },
  strengthen: {
    label: { en: "Strengthen the Brand", es: "Fortalecer la Marca", de: "Marke stärken" },
    items: [
      { id: "diagnostic",  label: { en: "Brand Diagnostic",         es: "Diagnóstico de Marca",      de: "Marken-Diagnose" },         page: "/brand-studio/strengthen/diagnostic" },
      { id: "positioning", label: { en: "AI Positioning",           es: "Posicionamiento con IA",    de: "KI-Positionierung" },       page: "/brand-studio/strengthen/positioning" },
      { id: "audience",    label: { en: "Audience Intelligence",    es: "Inteligencia de Audiencia", de: "Audience Intelligence" },   page: "/brand-studio/strengthen/audience" },
      { id: "competitor",  label: { en: "Competitor Intelligence",  es: "Inteligencia Competitiva",  de: "Wettbewerbs-Intelligence" },page: "/brand-studio/strengthen/competitor" },
    ],
  },
  health: {
    label: { en: "Measure Brand Health", es: "Mide la Salud de Marca", de: "Markengesundheit messen" },
    items: [
      { id: "brand-health",      label: { en: "Brand Health",      es: "Salud de Marca",       de: "Markengesundheit" },     page: "/brand-studio/brand-health" },
      { id: "brand-performance", label: { en: "Brand Performance", es: "Rendimiento de Marca", de: "Marken-Performance" },   page: "/brand-studio/brand-performance" },
    ],
  },
  steward: {
    label: { en: "Steward the Brand", es: "Custodiar la Marca", de: "Marke verwalten" },
    items: [
      { id: "living-bi", label: { en: "Living Brand Intelligence", es: "Inteligencia de Marca Viva", de: "Lebendige Marken-Intelligenz" }, page: "/brand-studio/steward/living-bi" },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Path → pillar resolver (used by Layer-2 strip + breadcrumb suppression).
// ─────────────────────────────────────────────────────────────────────────────
export function detectBrandPillar(pathname) {
  if (!pathname || !pathname.startsWith("/brand-studio")) return null;
  if (pathname.startsWith("/brand-studio/foundation")) return "foundation";
  if (pathname.startsWith("/brand-studio/protect")) return "protect";
  if (pathname.startsWith("/brand-studio/strengthen")) return "strengthen";
  if (pathname.startsWith("/brand-studio/brand-health") || pathname.startsWith("/brand-studio/brand-performance")) return "health";
  if (pathname.startsWith("/brand-studio/steward")) return "steward";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Path → stage resolver for the Build & Protect mission (Layer-1 highlighting).
// ─────────────────────────────────────────────────────────────────────────────
const BRAND_STAGE_MATCHERS = [
  ["/brand-studio/foundation", "foundation"],
  ["/brand-studio/protect", "protect"],
  ["/brand-studio/strengthen", "strengthen"],
  ["/brand-studio/brand-health", "health"],
  ["/brand-studio/brand-performance", "health"],
  ["/brand-studio/steward", "steward"],
];

export function detectBrandStage(pathname) {
  if (!pathname) return null;
  for (const [prefix, stageId] of BRAND_STAGE_MATCHERS) {
    if (pathname.startsWith(prefix)) return stageId;
  }
  return null;
}

export function isBrandStudioPath(pathname) {
  return !!pathname && pathname.startsWith("/brand-studio");
}

// ─────────────────────────────────────────────────────────────────────────────
// Grow the Brand — MARKETING STUDIO canonical breadcrumb overlay.
// (Phase 1 · Marketing rollout, paco-approved. Mirrors the Sales/Brand pattern
// per canonical-breadcrumb.md.) The Marketing Studio's own 5 sections become
// the mission pillars (studio-owned nouns, no cross-studio arc jumps). Stage
// ids match MarketingStudioPage SECTION_REGISTRY. This overlay leaves the
// broader GROW_THE_BRAND_MISSION (used by FlowBreadcrumb) untouched.
//
// PHASE-GATE: canonical rendering is scoped to MARKETING_CANONICAL_PATHS below
// (one leaf first). Expand the list as each leaf is approved.
// ─────────────────────────────────────────────────────────────────────────────
export const MARKETING_MISSION = {
  id: "grow-the-brand",
  label: { en: "Grow the Brand", es: "Hacer Crecer la Marca", de: "Die Marke ausbauen" },
  color: "#EC4899",
  stages: [
    { id: "diagnose", label: { en: "Diagnose", es: "Diagnosticar", de: "Diagnostizieren" }, subtitle: { en: "Scan & audit", es: "Escanear y auditar", de: "Scannen & prüfen" }, color: "#F59E0B", icon: "Search",        page: "/marketing/seo-analyzer" },
    { id: "plan",     label: { en: "Plan",     es: "Planear",      de: "Planen" },           subtitle: { en: "30-day rhythm", es: "Ritmo de 30 días", de: "30-Tage-Rhythmus" }, color: "#A855F7", icon: "ClipboardList", page: "/marketing/plans" },
    { id: "create",   label: { en: "Create",   es: "Crear",        de: "Erstellen" },        subtitle: { en: "Ship the work", es: "Lanza el trabajo", de: "Arbeit liefern" },  color: "#3B82F6", icon: "Wand2",        page: "/marketing/social-batch" },
    { id: "publish",  label: { en: "Publish",  es: "Publicar",     de: "Veröffentlichen" },  subtitle: { en: "Go live", es: "En vivo", de: "Live gehen" },                     color: "#F97316", icon: "Sparkles",     page: "/marketing/brand-launch" },
    { id: "measure",  label: { en: "Measure",  es: "Medir",        de: "Messen" },           subtitle: { en: "ROI & insights", es: "ROI e insights", de: "ROI & Insights" },   color: "#10B981", icon: "BarChart3",    page: "/marketing/roi" },
  ],
};

export const MARKETING_PILLAR_WORKFLOWS = {
  diagnose: {
    label: { en: "Diagnose the Market", es: "Diagnostica el Mercado", de: "Den Markt diagnostizieren" },
    items: [
      { id: "research",     label: { en: "Market Researcher", es: "Investigador de Mercado", de: "Markt-Forscher" }, page: "/marketing/research" },
      { id: "seo-analyzer", label: { en: "SEO Analyzer",      es: "Analizador SEO",          de: "SEO-Analyzer" },   page: "/marketing/seo-analyzer" },
    ],
  },
  plan: {
    label: { en: "Plan the Rhythm", es: "Planea el Ritmo", de: "Den Rhythmus planen" },
    items: [
      { id: "plan-agent",     label: { en: "Plan Agent",     es: "Agente de Plan",    de: "Plan-Agent" },      page: "/marketing/plan" },
      { id: "marketing-plan", label: { en: "Marketing Plan", es: "Plan de Marketing", de: "Marketing-Plan" }, page: "/marketing/plans" },
    ],
  },
  create: {
    label: { en: "Create the Work", es: "Crea el Trabajo", de: "Die Arbeit erstellen" },
    items: [
      { id: "social-batch",   label: { en: "Social Batch",   es: "Lote Social",      de: "Social-Batch" },     page: "/marketing/social-batch" },
      { id: "email-sequence", label: { en: "Email Sequence", es: "Secuencia Email",  de: "E-Mail-Sequenz" },   page: "/marketing/email-sequence" },
      { id: "landing",        label: { en: "Landing Page",   es: "Página de Aterrizaje", de: "Landing-Page" }, page: "/marketing/landing" },
    ],
  },
  publish: {
    label: { en: "Publish Live", es: "Publica en Vivo", de: "Live veröffentlichen" },
    items: [
      { id: "go-live", label: { en: "Campaign Go-Live", es: "Campaña en Vivo", de: "Kampagne Go-Live" }, page: "/marketing/brand-launch" },
    ],
  },
  measure: {
    label: { en: "Measure Results", es: "Mide Resultados", de: "Ergebnisse messen" },
    items: [
      { id: "roi",           label: { en: "ROI Tracker",   es: "Rastreador ROI",   de: "ROI-Tracker" },   page: "/marketing/roi" },
      { id: "marketing-hub", label: { en: "Marketing Hub", es: "Hub de Marketing", de: "Marketing-Hub" }, page: "/marketing/dashboard" },
    ],
  },
};

const MARKETING_STAGE_MATCHERS = [
  ["/marketing/research", "diagnose"],
  ["/marketing/seo-analyzer", "diagnose"],
  ["/marketing/plans", "plan"],
  ["/marketing/plan", "plan"],
  ["/marketing/social-batch", "create"],
  ["/marketing/email-sequence", "create"],
  ["/marketing/landing", "create"],
  ["/marketing/brand-launch", "publish"],
  ["/marketing/roi", "measure"],
  ["/marketing/dashboard", "measure"],
];

export function detectMarketingStage(pathname) {
  if (!pathname) return null;
  // CANON-STUDIO-VS-HUB / Marketing UI cleanup (2026-07-08):
  // Marketing Home is an overview ownership surface — the canonical bar renders
  // with NO active stage. Mailing Studio (/marketing) is an email distribution
  // surface → maps to the Publish stage. Both are exact-match only.
  if (pathname === "/marketing-studio") return null;
  if (pathname === "/marketing") return "publish";
  for (const [prefix, stageId] of MARKETING_STAGE_MATCHERS) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return stageId;
  }
  return null;
}

export function detectMarketingPillar(pathname) {
  return detectMarketingStage(pathname);
}

// Full Marketing Studio canonical set (paco-approved expansion). Every real
// Marketing Studio surface renders the 2-row canonical breadcrumb; Brand-owned
// routes under /marketing/*, Web Studio, redirects and editors are EXCLUDED.
export const MARKETING_CANONICAL_PATHS = [
  "/marketing-studio",
  "/marketing",
  "/marketing/research",
  "/marketing/seo-analyzer",
  "/marketing/plan",
  "/marketing/plans",
  "/marketing/social-batch",
  "/marketing/email-sequence",
  "/marketing/landing",
  "/marketing/brand-launch",
  "/marketing/roi",
  "/marketing/dashboard",
];

export function isMarketingStudioPath(pathname) {
  return !!pathname && MARKETING_CANONICAL_PATHS.includes(pathname);
}

// Cross-studio owner badge labels for the Mission Breadcrumb Row 1 (AO-001).
// `marketing` (home studio) intentionally has NO badge.
export const STAGE_OWNER_BADGE = {
  brand:    { en: "Brand",    es: "Brand",      de: "Brand" },
  creative: { en: "Creative", es: "Creative",   de: "Creative" },
  shared:   { en: "Shared",   es: "Compartido", de: "Geteilt" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Win the Client mission canon (Sprint 2026-06-11 PM-9 Phase 3B, paco-approved).
// Backend `loop_taxonomy.py` already defines the 6 stage *ids* — this overlay
// adds the labels / colors / icons / subtitles + per-stage page targets so
// the canonical breadcrumb (canonical-breadcrumb.md) can render Sales Studio
// the same way Brand renders.
//
// paco 2026-06-11: the LABEL for the `lead` id is "Prospect" (not "Lead").
// The id stays `lead` for backwards-compat with loop_taxonomy.py.
// ─────────────────────────────────────────────────────────────────────────────
export const WIN_THE_CLIENT_MISSION = {
  id: "win-the-client",
  label: { en: "Win the Client", es: "Ganar al Cliente", de: "Den Kunden gewinnen" },
  color: "#3B82F6", // blue — mission-level accent (matches loop_taxonomy.py)
  icon: "Target",
  is_magic: false,
  studio_path: "/sales-studio",
  stages: [
    {
      id: "lead",
      color: "#3B82F6", // blue — capture interest
      icon: "UserPlus",
      subtitle: { en: "Capture interest", es: "Capta el interés",     de: "Interesse erfassen" },
      label:    { en: "Prospect",         es: "Prospectar",           de: "Akquise" },
      page: "/leads",
    },
    {
      id: "concept",
      color: "#8B5CF6", // violet — shape the idea
      icon: "Lightbulb",
      subtitle: { en: "Shape the idea",   es: "Da forma a la idea",   de: "Idee formen" },
      label:    { en: "Concept",          es: "Concepto",             de: "Konzept" },
      page: "/concept-room",
    },
    {
      id: "quote",
      color: "#06B6D4", // cyan — set the price
      icon: "FileText",
      subtitle: { en: "Set the price",    es: "Define el precio",     de: "Preis festlegen" },
      label:    { en: "Quote",            es: "Cotizar",              de: "Angebot" },
      page: "/quotes",
    },
    {
      id: "proposal",
      color: "#EC4899", // pink — sell the story
      icon: "FileSignature",
      subtitle: { en: "Sell the story",   es: "Vende la historia",    de: "Geschichte verkaufen" },
      label:    { en: "Proposal",         es: "Propuesta",            de: "Angebot" },
      page: "/proposal-builder",
    },
    {
      id: "pitch",
      color: "#F59E0B", // amber — land the room
      icon: "Presentation",
      subtitle: { en: "Land the room",    es: "Conquista la sala",    de: "Den Raum gewinnen" },
      label:    { en: "Pitch",            es: "Presentar",            de: "Pitch" },
      page: "/one-page-pitches",
    },
    {
      id: "win",
      color: "#10B981", // emerald — close & kickoff
      icon: "Trophy",
      subtitle: { en: "Close & kickoff",  es: "Cierra y arranca",     de: "Abschluss & Start" },
      label:    { en: "Win",              es: "Ganar",                de: "Gewinnen" },
      page: "/kanban",
    },
  ],
};

// Layer-2 workflow strips — one per Sales stage. Children = sub-pages an
// operator works through inside that stage. Routes verified against App.js.
export const SALES_PILLAR_WORKFLOWS = {
  lead: {
    label: { en: "Prospect the Deal", es: "Prospecta el Trato", de: "Geschäft anbahnen" },
    items: [
      { id: "leads",    label: { en: "Leads",    es: "Prospectos", de: "Leads" },     page: "/leads" },
      { id: "contacts", label: { en: "Contacts", es: "Contactos",  de: "Kontakte" },  page: "/contacts" },
      { id: "briefs",   label: { en: "Briefs",   es: "Resúmenes",  de: "Briefings" }, page: "/briefs" },
    ],
  },
  concept: {
    label: { en: "Shape the Concept", es: "Da Forma al Concepto", de: "Konzept formen" },
    items: [
      { id: "concept-room", label: { en: "Concept Room", es: "Sala de Concepto", de: "Konzept-Raum" }, page: "/concept-room" },
    ],
  },
  quote: {
    label: { en: "Set the Quote", es: "Define la Cotización", de: "Angebot definieren" },
    items: [
      { id: "quotes",  label: { en: "Quotes",  es: "Cotizaciones", de: "Angebote" },     page: "/quotes" },
      // Sprint 2026-06-12 PM-10.7 (paco) — sidebar wires Forms label to /intake-forms.
      // Both /forms and /intake-forms render IntakeFormsPage; we surface the canonical
      // sidebar URL here so Row 2 highlights "Forms" when the user clicked the sidebar.
      { id: "forms",   label: { en: "Forms",   es: "Formularios",  de: "Formulare" },    page: "/intake-forms" },
    ],
  },
  proposal: {
    label: { en: "Build the Proposal", es: "Construye la Propuesta", de: "Angebot erstellen" },
    items: [
      { id: "proposal-builder",   label: { en: "Proposal Builder",   es: "Constructor",            de: "Angebots-Builder" },   page: "/proposal-builder" },
      // Sprint 2026-06-12 PM-10.7 (paco) — Proposal Analytics belongs in Row 2 alongside Builder.
      { id: "proposal-analytics", label: { en: "Proposal Analytics", es: "Analítica de Propuestas", de: "Proposal-Analytik" }, page: "/proposal-analytics" },
    ],
  },
  pitch: {
    label: { en: "Deliver the Pitch", es: "Entrega el Pitch", de: "Pitch liefern" },
    items: [
      { id: "one-page-pitches", label: { en: "One-Page Pitches", es: "One-Page Pitches", de: "One-Page Pitches" }, page: "/one-page-pitches" },
      // Sprint 2026-06-12 PM-10.5 — `OnePagePitchesPage` handles BOTH one-pagers
      // and decks via type filter; there is no standalone `/pitch-decks` route.
      { id: "pitch-decks",      label: { en: "Pitch Decks",      es: "Pitch Decks",      de: "Pitch-Decks" },      page: "/one-page-pitches?type=deck" },
    ],
  },
  win: {
    label: { en: "Close & Kickoff", es: "Cierra y Arranca", de: "Abschluss & Start" },
    items: [
      { id: "kanban",          label: { en: "Pipeline",        es: "Pipeline",          de: "Pipeline" },           page: "/kanban" },
      // Client Health lives at `/administration/health` (ClientHealthPage). See sales-mission-canon.md.
      { id: "client-health",   label: { en: "Client Health",   es: "Salud del Cliente", de: "Kundenstatus" },       page: "/administration/health" },
      // Sprint 2026-06-12 PM-10.7 (paco) — Sales Calendar + Sales Analytics moved
      // INTO the Win-stage children row (reversing PM-10.6's §2 meta-view exclusion).
      { id: "sales-calendar",  label: { en: "Sales Calendar",  es: "Calendario",        de: "Vertriebs-Kalender" }, page: "/calendar" },
      // Sprint 2026-06-12 PM-10.8 (paco) — Calendar Hub is a separate sidebar entry
      // wired to /meetings (MeetingsPage). Distinct from Sales Calendar (/calendar).
      { id: "calendar-hub",    label: { en: "Calendar Hub",    es: "Hub de Calendarios", de: "Kalender-Hub" },       page: "/meetings" },
      { id: "sales-analytics", label: { en: "Sales Analytics", es: "Analítica",         de: "Vertriebs-Analytik" }, page: "/analytics" },
    ],
  },
};

const SALES_STAGE_MATCHERS = [
  ["/leads", "lead"],
  ["/contacts", "lead"],
  ["/briefs", "lead"],
  ["/concept-room", "concept"],
  ["/quotes", "quote"],
  ["/forms", "quote"],
  ["/intake-forms", "quote"],          // Sprint 2026-06-12 PM-10.7 (paco) — sidebar links Forms → /intake-forms
  ["/proposal-builder", "proposal"],
  ["/proposal-analytics", "proposal"], // Sprint 2026-06-12 PM-10.7 (paco) — UnifiedAnalyticsPage for proposals
  ["/one-page-pitches", "pitch"],
  ["/kanban", "win"],
  ["/administration/health", "win"],
  // Sprint 2026-06-12 PM-10.7 (paco override) — these are Sales-cluster
  // destinations and DO get the canonical breadcrumb. Earlier PM-10.6
  // §2 "meta-views excluded" stance is reversed by paco UX decision.
  ["/calendar", "win"],                // Sales Calendar / Calendar Hub
  ["/meetings", "win"],                // Sprint 2026-06-12 PM-10.8 (paco) — CalendarHub sidebar entry → MeetingsPage
  ["/analytics", "win"],               // Sales Analytics
  ["/sales-studio", "lead"],           // landing defaults to first stage
];

export function detectSalesStage(pathname) {
  if (!pathname) return null;
  for (const [prefix, stageId] of SALES_STAGE_MATCHERS) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return stageId;
  }
  return null;
}

export function detectSalesPillar(pathname) {
  // For Sales the "pillar" === "stage" (flat funnel, no nested grouping).
  return detectSalesStage(pathname);
}

export function isSalesStudioPath(pathname) {
  if (!pathname) return false;
  return SALES_STAGE_MATCHERS.some(([prefix]) => pathname === prefix || pathname.startsWith(prefix + "/"));
}

// ─────────────────────────────────────────────────────────────────────────────
// Mission Architecture Alignment (2026-06-03) — public helpers used by every
// surface that lists missions. The backend `loop_taxonomy.py` still ships 5
// missions; these helpers splice the Build & Protect mission in BEFORE the
// `grow-the-brand` row/card so the operator always sees the 6-mission reality.
//
// Contract (ADR-017): NO surface in the app may render a mission list without
// going through one of these helpers (or `/api/loop/journeys`). Hardcoded
// mission arrays are forbidden.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the canonical 6-mission list for card grids (TodaysMissionTab,
 * MorningBriefModal, RoleMissionControlTab). The synthetic Brand mission
 * is placed FIRST (Brand is the architectural foundation, per ADR-017).
 * Each mission object matches the shape the backend `/api/loop/missions`
 * returns, plus `_frontendOnly: true` on the Brand entry so consumers can
 * detect synthetic missions.
 */
export function injectBrandMission(backendMissions) {
  if (!Array.isArray(backendMissions)) return [{ ...BUILD_AND_PROTECT_MISSION, _frontendOnly: true }];
  const synthetic = { ...BUILD_AND_PROTECT_MISSION, _frontendOnly: true };
  // Skip if already injected (idempotent).
  if (backendMissions.some(m => m?.id === synthetic.id)) return backendMissions;
  // Brand FIRST (architectural foundation).
  return [synthetic, ...backendMissions];
}

/**
 * Returns the canonical 6-row list for the Mission Pulse heatmap. The
 * synthetic Brand row is placed FIRST (architectural foundation,
 * per ADR-017).
 *
 * `existingRows` are the rows returned by `/api/mission-pulse/heatmap`.
 * The Brand row mirrors the row shape used by the heatmap, with 5 stage
 * pills (Foundation/Protect/Strengthen/Health & Performance/Steward).
 * Counts default to 0 — wave 2 will hydrate them from
 * `/api/brand/grow/deployment-receipt`.
 */
export function applyBrandMissionSplit(existingRows, lang = "en") {
  if (!Array.isArray(existingRows)) return existingRows;
  const id = BUILD_AND_PROTECT_MISSION.id;
  // Idempotent.
  if (existingRows.some(r => r?.mission_id === id)) return existingRows;
  const stages = BUILD_AND_PROTECT_MISSION.stages.map(s => ({
    id: s.id,
    label: s.label?.[lang] || s.label?.en || s.id,
    count: 0,
    page: s.page,
    is_milestone_only: false,
  }));
  const syntheticRow = {
    mission_id: id,
    mission_label:
      BUILD_AND_PROTECT_MISSION.label?.[lang] ||
      BUILD_AND_PROTECT_MISSION.label?.en,
    studio_path: BUILD_AND_PROTECT_MISSION.studio_path,
    accent: BUILD_AND_PROTECT_MISSION.color,
    icon: BUILD_AND_PROTECT_MISSION.icon,
    is_magic: false,
    stages,
    _frontendOnly: true,
  };
  // Brand FIRST (architectural foundation).
  return [syntheticRow, ...existingRows];
}

/**
 * Returns the canonical 6-journey list for the BusinessJourneysModal. The
 * synthetic Brand journey is placed FIRST (Brand is the architectural
 * foundation, per paco 2026-06-03). Each journey carries `id`, `label`,
 * `color`, `studio_path`, and `stages[]` with `{ id, label, page, icon }`.
 */
export function injectBrandJourney(backendJourneys) {
  const synthetic = {
    id: BUILD_AND_PROTECT_MISSION.id,
    label: BUILD_AND_PROTECT_MISSION.label,
    tagline: BUILD_AND_PROTECT_MISSION.tagline,
    color: BUILD_AND_PROTECT_MISSION.color,
    icon: BUILD_AND_PROTECT_MISSION.icon,
    is_magic: false,
    studio_path: BUILD_AND_PROTECT_MISSION.studio_path,
    stages: BUILD_AND_PROTECT_MISSION.stages.map(s => ({
      id: s.id,
      label: s.label,
      page: s.page,
      icon: null,
    })),
    _frontendOnly: true,
  };
  if (!Array.isArray(backendJourneys)) return [synthetic];
  if (backendJourneys.some(j => j?.id === synthetic.id)) return backendJourneys;
  // Brand FIRST (architectural foundation).
  return [synthetic, ...backendJourneys];
}

/**
 * Synthesize a mission-detail payload (the `/api/loop/missions/{id}`
 * response shape) for the synthetic Brand mission. Used by MissionFlowModal
 * to short-circuit the backend fetch when the user opens the Brand card.
 */
export function getBrandMissionDetail() {
  return {
    ok: true,
    mission: { ...BUILD_AND_PROTECT_MISSION, _frontendOnly: true },
    stages: BUILD_AND_PROTECT_MISSION.stages.map(s => ({
      id: s.id,
      label: s.label,
      page: s.page,
      is_milestone_only: false,
    })),
    studio_path: BUILD_AND_PROTECT_MISSION.studio_path,
    stage_source: "brand-frontend",
  };
}

/**
 * Returns true if the mission is the synthetic frontend-only Brand mission.
 * Consumers use this to skip backend fetches that would 404.
 */
export function isFrontendOnlyMission(mission) {
  if (!mission) return false;
  return !!mission._frontendOnly || mission.id === BUILD_AND_PROTECT_MISSION.id;
}
